import { DayButton } from "@/components/Inputs/RangePicker/components/DayButton/DayButton.tsx";
import { Footer } from "@/components/Inputs/RangePicker/components/Footer/Footer.tsx";
import { getDateBoundary } from "@/components/Inputs/RangePicker/helpers/getDateBoundary.ts";
import { useHoverStore } from "@/components/Inputs/RangePicker/stores/useHoverStore.ts";
import { filterDateDefault } from "@/helpers/filterDates.ts";
import { locales } from "@/helpers/locales.ts";
import { isAfter, isBefore, isSameDay } from "date-fns";
import format from "date-fns/format";
import i18n from "i18next";
import capitalize from "lodash/capitalize";
import { useCallback, useMemo } from "react";
import { DayPickerProps } from "react-day-picker";
import { useShallow } from "zustand/react/shallow";

interface UseRangePickerDayPickerPropsParams {
  startDate: Date | null;
  endDate: Date | null;
  onChange?: (dates: [Date | null, Date | null]) => void;
  filterDate?: (date: Date) => boolean;
  setHideDatePicker: (value: boolean) => void;
}

export const useRangePickerDayPickerProps = ({
  startDate,
  endDate,
  onChange,
  filterDate,
  setHideDatePicker,
}: UseRangePickerDayPickerPropsParams): DayPickerProps => {
  const [hoveredDate] = useHoverStore(useShallow((state) => [state.hoveredDate]));

  const handleChange = useCallback(
    (dates: [Date | null, Date | null]) => {
      if (startDate && endDate) {
        if (dates[0] && dates[1]) {
          if (isSameDay(startDate, dates[0]) && !isSameDay(endDate, dates[1])) {
            return onChange?.([dates[1], null]);
          } else if (!isSameDay(startDate, dates[0]) && isSameDay(endDate, dates[1])) {
            return onChange?.([dates[0], null]);
          }
        }
      }
      onChange?.(dates);

      if (dates[0] && dates[1]) {
        setHideDatePicker(true);
      }
    },
    [onChange, startDate, endDate, setHideDatePicker],
  );

  const locale = locales[i18n.language];

  return useMemo(
    () =>
      ({
        onSelect: (selected: { from: Date; to: Date } | undefined) =>
          selected && handleChange([selected.from, selected.to]),
        captionLayout: "dropdown",
        mode: "range",
        components: { DayButton: DayButton },
        modifiers: {
          inHoveredBoundary: (date: Date) => {
            if (!startDate || !hoveredDate) return false;
            const [start, end] = getDateBoundary([startDate, endDate], hoveredDate);

            return start && end ? isAfter(date, start) && isBefore(date, end) : false;
          },
        },
        numberOfMonths: 2,
        locale,
        modifiersClassNames: {
          inHoveredBoundary: "rdp-range_middle",
        },
        selected: { from: startDate!, to: endDate! },
        defaultMonth: startDate ?? new Date(),
        startMonth: new Date(),
        endMonth: new Date(new Date().setFullYear(new Date().getFullYear() + 2, 11)),
        disabled: filterDate ?? filterDateDefault,
        formatters: {
          formatMonthDropdown: (monthNumber: number, locale?: Locale) =>
            capitalize(
              format(new Date().setMonth(monthNumber, 2), "LLLL", {
                locale,
              }),
            ),
        },
        footer: (
          <Footer
            startDate={startDate}
            endDate={endDate}
            onChange={onChange}
            hoveredDate={hoveredDate}
            setHideDatePicker={setHideDatePicker}
          />
        ),
      }) as DayPickerProps,
    [startDate, endDate, handleChange, locale, filterDate, onChange, hoveredDate, setHideDatePicker],
  );
};
